import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import { FiBook, FiShoppingCart, FiCheck, FiFilter, FiSearch, FiTag, FiClock, FiGrid, FiStar, FiUsers, FiTrendingUp } from 'react-icons/fi';
import { API_BASE_URL, BASE_URL } from '../config/config';
const AllCourses = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  

  const isLoggedIn = !!user;

  // Helper function to strip HTML tags and decode HTML entities
  const stripHtml = (html) => {
    if (!html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  useEffect(() => {
    fetchCategories();
    
    // Handle category from URL
    const categoryFromUrl = searchParams.get('category');
    if (categoryFromUrl) {
      setSelectedCategory(categoryFromUrl);
      fetchCoursesByCategory(categoryFromUrl);
    } else {
      fetchAllCourses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/coursecategory`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAllCourses = async () => {
    try {
      setLoading(true);
      const email = isLoggedIn ? user.email : 'guest@example.com';
      const response = await fetch(`${API_BASE_URL}/popularcourse?email=${email}&type=all`);
      const data = await response.json();
      if (data.success) {
        setCourses(data.data);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoursesByCategory = async (categoryId) => {
    try {
      setLoading(true);
      const email = isLoggedIn ? user.email : 'guest@example.com';
      const url = categoryId === 'all' 
        ? `${API_BASE_URL}/popularcourse?email=${email}&type=all`
        : `${API_BASE_URL}/coursebycategory/${categoryId}?email=${email}`;
      
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setCourses(data.data);
      }
      setSelectedCategory(categoryId);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses.filter(course =>
    course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    course.tradeName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <PublicHeader />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-purple-600 text-white py-12 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -mr-48 -mt-48"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full -ml-48 -mb-48"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 flex items-center justify-center gap-3">
              <FiBook className="w-10 h-10" />
              Explore Our Courses
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
              Discover the perfect course to boost your career and achieve your learning goals
            </p>
          </div>

          {/* Search Bar - Enhanced */}
          <div className="max-w-3xl mx-auto">
            <div className="relative group">
              <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 z-10" />
              <input
                type="text"
                placeholder="Search for courses, topics, or skills..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-xl border-0 shadow-2xl text-gray-900 placeholder-gray-500 focus:ring-4 focus:ring-white/30 transition-all text-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Section - Enhanced */}
      <div className="bg-white border-b sticky top-[0px] z-40 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-gray-700">
              <FiFilter className="w-5 h-5" />
              <span className="font-semibold">Filter by Category:</span>
            </div>
            {!loading && filteredCourses.length > 0 && (
              <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                {filteredCourses.length} {filteredCourses.length === 1 ? 'Course' : 'Courses'}
              </span>
            )}
          </div>

          {/* Category Filter Pills - Enhanced */}
          <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
            <button
              onClick={() => fetchCoursesByCategory('all')}
              className={`px-5 py-2.5 rounded-full font-semibold whitespace-nowrap transition-all transform hover:scale-105 ${
                selectedCategory === 'all'
                  ? 'bg-gradient-to-r from-primary-500 to-purple-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow'
              }`}
            >
              <span className="flex items-center gap-2">
                <FiGrid className="w-4 h-4" />
                All Courses
              </span>
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => fetchCoursesByCategory(category.id)}
                className={`px-5 py-2.5 rounded-full font-semibold whitespace-nowrap transition-all transform hover:scale-105 ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-r from-primary-500 to-purple-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow'
                }`}
              >
                {category.type}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Courses Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="card animate-pulse">
                <div className="bg-gray-200 h-48 rounded-lg mb-4"></div>
                <div className="space-y-3">
                  <div className="bg-gray-200 h-6 rounded w-3/4"></div>
                  <div className="bg-gray-200 h-4 rounded w-full"></div>
                  <div className="bg-gray-200 h-4 rounded w-2/3"></div>
                  <div className="bg-gray-200 h-10 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="card text-center py-20 bg-gradient-to-br from-gray-50 to-white">
            <div className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
              <FiBook className="w-16 h-16 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">No Courses Found</h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              We couldn't find any courses matching your search. Try adjusting your filters or search term.
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                fetchCoursesByCategory('all');
              }}
              className="btn-primary inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-purple-600 hover:from-primary-600 hover:to-purple-700"
            >
              <FiGrid className="w-5 h-5" />
              Show All Courses
            </button>
          </div>
        ) : (
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredCourses.map((course) => {
              const finalPrice = (course.price - (course.price * course.discount / 100)).toFixed(2);
              const isPurchased = course.purchased > 0;

              return (
                <div key={course.id} className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:-translate-y-2 border border-gray-100">
                  {/* Course Thumbnail */}
                  <Link to={`/course-details/${course.id}`} className="block relative overflow-hidden">
                    <div className="relative h-48 overflow-hidden bg-gradient-to-br from-primary-100 to-purple-100">
                      <img
                        src={`${BASE_URL}/${course.course_thumbnail}`}
                        alt={course.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%236366f1" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="white" font-size="24"%3ECourse%3C/text%3E%3C/svg%3E';
                        }}
                      />
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                      {/* Discount Badge */}
                      {course.discount > 0 && (
                        <div className="absolute top-3 left-3 bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
                          <FiTrendingUp className="w-3 h-3" />
                          {course.discount}% OFF
                        </div>
                      )}

                      {/* Purchase Status Badge */}
                      {isPurchased && (
                        <div className="absolute top-3 right-3 bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 shadow-lg">
                          <FiCheck className="w-3 h-3" />
                          OWNED
                        </div>
                      )}
                    </div>
                  </Link>

                  {/* Course Info */}
                  <div className="p-5">
                    <Link to={`/course-details/${course.id}`}>
                      <h3 className="text-base font-bold text-gray-800 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2 min-h-[48px] leading-tight">
                        {course.title}
                      </h3>
                    </Link>
                    
                    {course.description && (
                      <p className="text-xs text-gray-600 mb-3 line-clamp-2 leading-relaxed">
                        {stripHtml(course.description)}
                      </p>
                    )}

                    {/* Meta Info */}
                    <div className="flex items-center justify-between mb-4 gap-2">
                      <span className="text-xs bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-2.5 py-1 rounded-full font-semibold flex items-center gap-1 border border-blue-100">
                        <FiTag className="w-3 h-3" />
                        <span className="truncate max-w-[80px]">{course.tradeName || 'General'}</span>
                      </span>
                      {course.validity && (
                        <span className="text-xs text-gray-500 flex items-center gap-1 bg-gray-50 px-2.5 py-1 rounded-full">
                          <FiClock className="w-3 h-3" />
                          {course.validity} Months
                        </span>
                      )}
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-100 my-4"></div>

                    {/* Price Section */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-bold text-primary-600">
                            ₹{finalPrice}
                          </span>
                          {course.discount > 0 && (
                            <span className="text-sm text-gray-400 line-through">
                              ₹{course.price.toFixed(2)}
                            </span>
                          )}
                        </div>
                        {course.discount > 0 && (
                          <span className="text-xs bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 px-2 py-1 rounded-full font-bold border border-green-200">
                            Save ₹{(course.price - finalPrice).toFixed(0)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action Button */}
                    {isPurchased ? (
                      <Link
                        to={`/course/${course.id}`}
                        state={{ course }}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl font-semibold transition-all shadow-md hover:shadow-lg transform hover:scale-105"
                      >
                        <FiBook className="w-4 h-4" />
                        Open Course
                      </Link>
                    ) : (
                      <Link
                        to={`/course-details/${course.id}`}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-primary-500 to-purple-600 hover:from-primary-600 hover:to-purple-700 text-white rounded-xl font-semibold transition-all shadow-md hover:shadow-xl transform hover:scale-105"
                      >
                        <FiShoppingCart className="w-4 h-4" />
                        Enroll Now
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default AllCourses;

